import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEN } from '../constants';
import { DAI_TOI_DA, kiemTenVaiTro, loiTheoConTro } from '../nghiep-vu/vai-tro';
import type { Role } from '../types';

/**
 * Hộp thoại đổi tên vai trò (`PATCH /api/v1/roles/{id}`). Mã không đổi được:
 * hiện để người dùng biết đang sửa vai trò nào, kèm lý do.
 */
export function FormDoiTenVaiTro({
  vaiTro,
  open,
  dangLuu,
  onClose,
  onLuu,
}: {
  vaiTro: Role;
  open: boolean;
  dangLuu: boolean;
  onClose: () => void;
  onLuu: (name: string) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();

  const [ten, setTen] = useState(vaiTro.name);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);

  const maTen = kiemTenVaiTro(ten);
  const loiTen =
    (maTen && (daGui || maTen === 'dai') ? t(KHOA_LOI_TEN[maTen], { so: DAI_TOI_DA.name }) : undefined) ??
    loiServer?.truong['/name'];
  const coThayDoi = ten.trim() !== vaiTro.name;
  const loiChung = loiServer && !loiServer.truong['/name'] ? loiServer.loi : null;

  const gui = async () => {
    setDaGui(true);
    if (maTen || !coThayDoi || dangLuu) return;
    setLoiServer(null);
    try {
      await onLuu(ten.trim());
    } catch (err) {
      setLoiServer({ loi: err as ApiError, truong: loiTheoConTro((err as ApiError).details) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('vaiTroLop2.doiTenTieuDe', { ten: vaiTro.name })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              htmlType="submit"
              form={formId}
              loading={dangLuu}
              disabled={!coThayDoi}
            >
              {dangLuu ? t('chung.dangLuu') : t('vaiTroLop2.luuTen')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <Field label={t('vaiTroLop2.fTen')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              value={ten}
              onChange={(e) => {
                setTen(e.target.value);
                setLoiServer(null);
              }}
            />
          )}
        </Field>
        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-content-secondary">{t('vaiTroLop2.fMa')}</span>
          <span className="block break-all font-mono text-sm text-content-primary">{vaiTro.code}</span>
          <p className="text-xs text-content-muted">{t('vaiTroLop2.fMaKhongSua')}</p>
        </div>
      </form>
    </Dialog>
  );
}
